import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import styles from './AddStoreModal.module.css';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Store } from '../types';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';

interface AddStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedStore?: Store | null;
}

type DomainEntry = { id: string; value: string };

const DOMAIN_FORMAT = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

const newEntry = (value = ''): DomainEntry => ({ id: crypto.randomUUID(), value });

const AddStoreModal: React.FC<AddStoreModalProps> = ({ isOpen, onClose, onSuccess, selectedStore }) => {
  const { user } = useAuth();
  const [storeName, setStoreName] = useState('');
  const [domains, setDomains] = useState<DomainEntry[]>([newEntry()]);
  const [enableAutoExtract, setEnableAutoExtract] = useState(false);
  const [errors, setErrors] = useState<{ storeName?: string; domains?: string[]; enableAutoExtract?: string }>({});
  const prevStoreRef = useRef<Store | null | undefined>(null);

  useEffect(() => {
    if (selectedStore) {
      setStoreName(selectedStore.name);
      const existingDomains = selectedStore.store_domains.map((d) => newEntry(d.domain));
      setDomains(existingDomains.length > 0 ? existingDomains : [newEntry()]);
      setEnableAutoExtract(selectedStore.enable_auto_extract);
      setErrors({});
    } else if (!isOpen) {
      setStoreName('');
      setDomains([newEntry()]);
      setEnableAutoExtract(false);
      setErrors({});
    }
    prevStoreRef.current = selectedStore;
  }, [isOpen, selectedStore]);

  const handleDomainChange = (id: string, value: string) => {
    setDomains((prev) => prev.map((d) => (d.id === id ? { ...d, value } : d)));
  };

  const addDomain = () => {
    setDomains((prev) => [...prev, newEntry()]);
  };

  const removeDomain = (id: string) => {
    setDomains((prev) => prev.filter((d) => d.id !== id));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const newErrors: { storeName?: string; domains?: string[]; enableAutoExtract?: string } = {};

    if (!storeName.trim()) {
      newErrors.storeName = 'Store name is required.';
    }

    const domainErrors = domains.map((d) => {
      const trimmed = d.value.trim();
      if (trimmed === '') return '';
      return DOMAIN_FORMAT.test(trimmed) ? '' : 'Not a valid domain (e.g. takealot.com)';
    });

    if (domainErrors.some((e) => e !== '')) {
      newErrors.domains = domainErrors;
    }

    const filteredDomains = domains.map((d) => d.value.trim()).filter((v) => v.length > 0);
    if (enableAutoExtract && filteredDomains.length === 0) {
      newErrors.enableAutoExtract = "Can't be enabled without at least one domain.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const storeData: Record<string, unknown> = { name: storeName, domains: filteredDomains };
    if (user?.role === 'admin') {
      storeData.enable_auto_extract = enableAutoExtract;
    }

    try {
      const response = selectedStore
        ? await fetchWithAuth(`/stores/${selectedStore.id}`, {
            method: 'PATCH',
            body: JSON.stringify(storeData),
          })
        : await fetchWithAuth('/stores', {
            method: 'POST',
            body: JSON.stringify(storeData),
          });

      if (response.ok) {
        onSuccess();
        onClose();
        toast.success(`${storeName} ${selectedStore ? 'updated' : 'added'} successfully!`);
      } else {
        const data = await response.json().catch(() => ({}));
        const responseErrors: { storeName?: string; domains?: string[]; enableAutoExtract?: string } = {};
        if (data.domains?.length) responseErrors.domains = data.domains;
        if (data.enable_auto_extract?.length) responseErrors.enableAutoExtract = data.enable_auto_extract[0];
        if (Object.keys(responseErrors).length > 0) setErrors(responseErrors);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">{selectedStore ? 'Edit Store' : 'Add Store'}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="storeName" className="block text-gray-700">Store Name</label>
          <input
            id="storeName"
            type="text"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.storeName && <p className="text-red-500 text-sm mt-1">{errors.storeName}</p>}
        </div>

        <div>
          <label className="block text-gray-700 mb-1">Domains</label>
          <div className="space-y-2">
            {domains.map((domain, index) => (
              <div key={domain.id}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={domain.value}
                    onChange={(e) => handleDomainChange(domain.id, e.target.value)}
                    placeholder="e.g. takealot.com"
                    className={`flex-1 px-3 py-2 border rounded ${errors.domains?.[index] ? 'border-red-400' : ''}`}
                  />
                  {domains.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDomain(domain.id)}
                      className="text-red-500 hover:text-red-700 text-sm px-2"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {errors.domains?.[index] && (
                  <p className="text-red-500 text-sm mt-1">{errors.domains[index]}</p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addDomain}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            + Add another domain
          </button>
        </div>

        {(selectedStore || user?.role === 'admin') && (
          <div>
            <div className="flex items-center gap-2">
              <input
                id="enableAutoExtract"
                type="checkbox"
                checked={enableAutoExtract}
                onChange={(e) => setEnableAutoExtract(e.target.checked)}
                disabled={user?.role !== 'admin'}
                className="h-4 w-4 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="enableAutoExtract" className={`text-gray-700 ${user?.role !== 'admin' ? 'opacity-50' : ''}`}>Enable Auto Extract</label>
            </div>
            {errors.enableAutoExtract && <p className="text-red-500 text-sm mt-1">{errors.enableAutoExtract}</p>}
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <button type="button" onClick={onClose} className="bg-gray-300 text-gray-700 px-4 py-2 rounded">Cancel</button>
          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">{selectedStore ? 'Update Store' : 'Add Store'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default AddStoreModal;
